import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.DEV ? '' : 'https://ai-academy-api.bill-burkey.workers.dev';

export function useApi() {
  const { token } = useAuth();

  const fetchApi = useCallback(async <T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>)
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });

    // Check content-type before parsing JSON to handle HTML error pages
    const contentType = res.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      if (!res.ok) {
        throw new Error(`Server error (${res.status}): ${res.statusText}`);
      }
      // If response is OK but not JSON, return empty object
      return {} as T;
    }

    let data: T;
    try {
      data = await res.json();
    } catch {
      throw new Error(`Failed to parse response: ${res.status} ${res.statusText}`);
    }

    if (!res.ok) {
      throw new Error((data as { error?: string }).error || 'API request failed');
    }

    return data;
  }, [token]);

  return { fetchApi };
}

// Type definitions for API responses
export interface Track {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  level: number;
}

export interface Course {
  id: string;
  track_id: string;
  slug: string;
  title: string;
  description: string | null;
  level: number;
  lesson_count?: number;
}

export interface Lesson {
  id: string;
  course_id: string;
  slug: string;
  title: string;
  content: string | null;
  order_index: number;
  mode: 'NO_TOOL' | 'OPEN_AI' | 'AUDIT';
}

export interface Progress {
  id: string;
  lesson_id: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  last_updated: string;
}
